import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, 
  GraduationCap, 
  Building2, 
  Pill,
  Search,
  MapPin,
  ExternalLink,
  Target,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";

// Sample data - would come from API
const sampleAcademics = [
  { id: 1, name: "Dr. Sarah Chen", institution: "MIT", department: "Biology", focus: "Gene Therapy", location: "Cambridge, MA" },
  { id: 2, name: "Prof. Michael Torres", institution: "Stanford", department: "Bioengineering", focus: "mRNA Technology", location: "Palo Alto, CA" },
  { id: 3, name: "Dr. Emily Watson", institution: "Harvard Medical", department: "Oncology", focus: "Cancer Immunotherapy", location: "Boston, MA" },
];

const sampleBiotechs = [
  { id: 1, name: "Recursion Pharmaceuticals", focus: "AI Drug Discovery", stage: "Public", location: "Salt Lake City, UT", partnership_type: "Research" },
  { id: 2, name: "Beam Therapeutics", focus: "Base Editing", stage: "Public", location: "Cambridge, MA", partnership_type: "Licensing" },
  { id: 3, name: "Sana Biotechnology", focus: "Cell Therapy", stage: "Public", location: "Seattle, WA", partnership_type: "Co-development" },
];

const samplePharma = [
  { id: 1, name: "Roche", focus: "Oncology, Rare Diseases", hq: "Basel, Switzerland", partnership_interests: "Early-stage licensing, Co-development" },
  { id: 2, name: "Novartis", focus: "Gene Therapy, Cell Therapy", hq: "Basel, Switzerland", partnership_interests: "Platform partnerships, Acquisitions" },
  { id: 3, name: "Pfizer", focus: "mRNA, Oncology", hq: "New York, NY", partnership_interests: "Research collaborations, Licensing" },
];

function CollaboratorsContent({ user }) {
  const [activeTab, setActiveTab] = useState("academics");
  const [search, setSearch] = useState("");

  const handleFindMatches = () => {
    toast.info("AI-powered partner matching coming soon!", {
      description: "This will suggest collaborators based on your company profile and strategy."
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Collaborators</h1>
          <p className="text-slate-600 mt-1">Find academic partners, biotechs, and pharma companies</p>
        </div>
        <Button onClick={handleFindMatches} className="btn-secondary gap-2">
          <Sparkles className="w-4 h-4" />
          Find Suggested Partners
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-lg grid-cols-3 mb-6">
          <TabsTrigger value="academics" className="gap-2">
            <GraduationCap className="w-4 h-4" />
            <span className="hidden sm:inline">Academics</span>
          </TabsTrigger>
          <TabsTrigger value="biotechs" className="gap-2">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Biotechs</span>
          </TabsTrigger>
          <TabsTrigger value="pharma" className="gap-2">
            <Pill className="w-4 h-4" />
            <span className="hidden sm:inline">Big Pharma</span>
          </TabsTrigger>
        </TabsList>

        {/* Search Bar */}
        <Card className="border border-slate-200 mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={`Search ${activeTab}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Academics Tab */}
        <TabsContent value="academics">
          <div className="grid gap-4">
            {sampleAcademics.map((academic) => (
              <Card key={academic.id} className="border border-slate-200 hover:border-teal-300 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{academic.name}</h3>
                        <p className="text-sm text-slate-600">{academic.institution} • {academic.department}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">{academic.focus}</Badge>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{academic.location}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Card className="border border-dashed border-slate-300 bg-slate-50">
              <CardContent className="p-8">
                <GraduationCap className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">More Academics Coming Soon</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  We're building a comprehensive database of academic researchers and KOLs in biotech. 
                  Check back soon for more partnership opportunities.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Biotechs Tab */}
        <TabsContent value="biotechs">
          <div className="grid gap-4">
            {sampleBiotechs.map((biotech) => (
              <Card key={biotech.id} className="border border-slate-200 hover:border-teal-300 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{biotech.name}</h3>
                        <p className="text-sm text-slate-600">{biotech.focus}</p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge className="bg-purple-100 text-purple-700">{biotech.stage}</Badge>
                          <Badge variant="outline">{biotech.partnership_type}</Badge>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{biotech.location}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Card className="border border-dashed border-slate-300 bg-slate-50">
              <CardContent className="p-8">
                <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Expanding Biotech Database</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  We're adding more biotech companies with partnership opportunities. 
                  Our AI will soon match you with compatible partners based on your technology.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Big Pharma Tab */}
        <TabsContent value="pharma">
          <div className="grid gap-4">
            {samplePharma.map((pharma) => (
              <Card key={pharma.id} className="border border-slate-200 hover:border-teal-300 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Pill className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{pharma.name}</h3>
                        <p className="text-sm text-slate-600">{pharma.focus}</p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{pharma.hq}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          <strong>Interests:</strong> {pharma.partnership_interests}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Card className="border border-dashed border-slate-300 bg-slate-50">
              <CardContent className="p-8">
                <Pill className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Big Pharma Partnerships</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  We're cataloging partnership programs from major pharmaceutical companies. 
                  Soon you'll be able to find the right BD contact for your therapeutic area.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function Collaborators() {
  return (
    <AppLayout>
      {({ user }) => <CollaboratorsContent user={user} />}
    </AppLayout>
  );
}
